import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Alert,
  ScrollView,
  Pressable,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import OpeningPerformanceReport from '@/Components/OpeningPerformanceReport';
import CCColors from '@/Utils/CCColors';
import CCText from '@/Atoms/CCText';

import { useNavigation, useRoute } from '@react-navigation/native';
import { ScreenName } from '@/navigators/StackConstants';
import useGetOpeningIntelligenceReportApi from '@/Hooks/AssessmentApi/useGetOpeningIntelligenceReportApi';
import { getLocalStorage, downloadPDF } from '@/Utils/CommonUtils';
import ChessRestClient from '@/Api/ChessRestClient';
import CCButton from '@/Atoms/CCButton';
import { oirPrincipleToCannedQuery } from '@/Utils/oirPrincipleToCannedQuery';
import { useMediaQuery } from '@/Hooks/useMediaQuery/useMediaQuery';

const OpeningPerformanceReportScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<any>();
  const routeAssessmentId = route?.params?.assessmentId;
  const routeTournamentId = route?.params?.tournamentId;

  const [webAssessmentId, setWebAssessmentId] = useState<string | undefined>(undefined);
  const assessmentId = routeAssessmentId || webAssessmentId;

  const [tournamentId, setTournamentId] = useState<number | string | undefined>(
    routeTournamentId,
  );

  useEffect(() => {
    if (routeTournamentId != null && routeTournamentId !== '') {
      setTournamentId(routeTournamentId);
    }
  }, [routeTournamentId]);

  useEffect(() => {
    if (assessmentId || tournamentId != null) {
      return;
    }
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search || '');
      const aid = params.get('assessmentId') || params.get('assessment_id');
      if (aid) {
        setWebAssessmentId(aid);
      }
      const q = params.get('tournamentId') ?? params.get('oirTournament');
      if (q) {
        const n = parseInt(q, 10);
        if (!Number.isNaN(n)) {
          setTournamentId(n);
        }
      }
    }
  }, [assessmentId, tournamentId]);

  /** Web: `?refreshOirPdf=1` (or `oirPdfRefresh=1`) sends force_regenerate to bypass cached PDF for testing. */
  const [refreshOirPdfFromQuery, setRefreshOirPdfFromQuery] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const read = () => {
      const p = new URLSearchParams(window.location.search || '');
      setRefreshOirPdfFromQuery(
        p.get('refreshOirPdf') === '1' || p.get('oirPdfRefresh') === '1',
      );
    };
    read();
    window.addEventListener('popstate', read);
    return () => window.removeEventListener('popstate', read);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (assessmentId || tournamentId != null) {
        return;
      }
      const stored = await getLocalStorage('tournamentId');
      if (cancelled || stored == null || stored === '') {
        return;
      }
      const n = parseInt(String(stored), 10);
      if (!Number.isNaN(n)) {
        setTournamentId(n);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assessmentId, tournamentId]);

  const queryEnabled = Boolean(assessmentId || tournamentId != null);

  const {
    data: oirResponse,
    isLoading,
    isError,
    error,
  } = useGetOpeningIntelligenceReportApi({
    assessmentId,
    tournamentId,
    enabled: queryEnabled,
  });
  const frontendView = oirResponse?.data?.frontend_view || {};
  const { isMobileOrTab } = useMediaQuery();
  const [pdfWorking, setPdfWorking] = useState(false);

  const handleDownloadPdf = useCallback(async () => {
    const aid = oirResponse?.data?.assessment_id;
    if (!aid || pdfWorking) {
      return;
    }
    const safeBase = String(frontendView?.selected_opening || 'opening-report')
      .replace(/[^\w\-]+/g, '-')
      .slice(0, 60);
    const safeFileName = `${safeBase}.pdf`;

    const tidFromRoute =
      tournamentId != null && tournamentId !== ''
        ? typeof tournamentId === 'string'
          ? parseInt(tournamentId, 10)
          : Number(tournamentId)
        : NaN;
    const tidFromMeta = oirResponse?.data?.meta?.tournament_id;
    const metaNum =
      tidFromMeta != null && tidFromMeta !== ''
        ? typeof tidFromMeta === 'string'
          ? parseInt(tidFromMeta, 10)
          : Number(tidFromMeta)
        : NaN;
    let tournamentIdForPdf: number | undefined;
    if (!Number.isNaN(tidFromRoute) && tidFromRoute !== 0) {
      tournamentIdForPdf = tidFromRoute;
    } else if (!Number.isNaN(metaNum) && metaNum !== 0) {
      tournamentIdForPdf = metaNum;
    }

    setPdfWorking(true);
    try {
      const body: Record<string, string | number | undefined> = {
        assessment_id: aid,
        send_whatsapp: 0,
      };
      if (tournamentIdForPdf != null) {
        body.tournamentId = tournamentIdForPdf;
      }
      if (Platform.OS === 'web' && refreshOirPdfFromQuery) {
        body.force_regenerate = 1;
      }
      // Server HTML (WeasyPrint) PDF: returns cached URL or generates synchronously and caches.
      const req = await ChessRestClient.post(
        'assessment/v1/learning/request_opening_intelligence_report_pdf/',
        body,
      );

      const pdfUrl = req.data?.pdf_url as string | undefined;
      if (req.data?.success && pdfUrl) {
        downloadPDF(pdfUrl, safeFileName);
        return;
      }

      Alert.alert(
        'PDF unavailable',
        typeof req.data?.error === 'string'
          ? req.data.error
          : 'Could not build or retrieve the PDF. Check your connection and try again.',
      );
    } catch (e: any) {
      console.error(e);
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Something went wrong while preparing your PDF.';
      Alert.alert('PDF', typeof msg === 'string' ? msg : 'Request failed.');
    } finally {
      setPdfWorking(false);
    }
  }, [
    oirResponse,
    tournamentId,
    pdfWorking,
    frontendView?.selected_opening,
    refreshOirPdfFromQuery,
  ]);

  const toCountRows = (rows: Array<{title?: string; count_label?: string; count?: number}> = []) =>
    rows.map(row => ({
      title: row.title || '',
      count: row.count_label || `${row.count || 0}`,
    }));

  if (!queryEnabled) {
    return (
      <View style={styles.stateRoot}>
        <ScrollView contentContainerStyle={styles.stateScroll} showsVerticalScrollIndicator={false}>
          <View style={styles.stateCard}>
            <View style={styles.stateIconWrap}>
              <Ionicons
                name="information-circle-outline"
                size={40}
                color={CCColors.Primary.Default}
              />
            </View>
            <CCText style={styles.stateCardTitle} textAlign="center">
              How to open this report
            </CCText>
            <CCText style={styles.stateCardBody} textAlign="center">
              Open this screen with a tournament id: use{' '}
              <CCText style={styles.mono}>/opening-report/3539</CCText> in the address bar (if your
              app uses path routing), add{' '}
              <CCText style={styles.mono}>?tournamentId=3539</CCText> or{' '}
              <CCText style={styles.mono}>?assessmentId=uuid</CCText> to the URL, pass{' '}
              <CCText style={styles.mono}>tournamentId</CCText> in navigation params, or set{' '}
              <CCText style={styles.mono}>tournamentId</CCText> in local storage. Ensure{' '}
              <CCText style={styles.mono}>CHESS_API_URL</CCText> points at your API and you are
              logged in as the same user the report was built for.
            </CCText>
          </View>
        </ScrollView>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.stateRoot}>
        <View style={styles.loadingBlock}>
          <ActivityIndicator size="large" color={CCColors.Primary.Default} />
          <CCText style={styles.loadingLabel}>Loading opening report…</CCText>
        </View>
      </View>
    );
  }

  if (isError) {
    const err = error as {
      response?: { status?: number; data?: { error?: string } };
      message?: string;
    };
    const status = err?.response?.status;
    const apiMsg = err?.response?.data?.error;
    const isReportMissing =
      status === 404 ||
      (typeof apiMsg === 'string' &&
        apiMsg.toLowerCase().includes('not found'));

    if (isReportMissing) {
      const ctx = tournamentId != null ? 'tournament' : 'assessment';
      return (
        <View style={styles.stateRoot}>
          <ScrollView contentContainerStyle={styles.stateScroll} showsVerticalScrollIndicator={false}>
            <View style={styles.stateCard}>
              <View style={styles.stateIconWrap}>
                <Ionicons
                  name="hourglass-outline"
                  size={44}
                  color={CCColors.Primary.Default}
                />
              </View>
              <CCText style={styles.stateCardTitle} textAlign="center">
                Opening report not ready
              </CCText>
              <CCText style={styles.stateCardBody} textAlign="center">
                {`This opening performance report has not been generated yet for this ${ctx}. It is created after your games are analyzed — please check back soon.`}
              </CCText>
              <Pressable
                onPress={() => {
                  if ((navigation as any).canGoBack?.()) {
                    navigation.goBack();
                  } else {
                    (navigation as any).navigate(ScreenName.Dashboard);
                  }
                }}
                style={({ pressed }) => [
                  styles.secondaryButton,
                  pressed && styles.secondaryButtonPressed,
                ]}>
                <CCText style={styles.secondaryButtonLabel} textAlign="center">
                  Back to dashboard
                </CCText>
              </Pressable>
            </View>
          </ScrollView>
        </View>
      );
    }

    return (
      <View style={styles.stateRoot}>
        <ScrollView contentContainerStyle={styles.stateScroll} showsVerticalScrollIndicator={false}>
          <View style={styles.stateCard}>
            <View style={styles.stateIconWrap}>
              <Ionicons name="cloud-offline-outline" size={40} color={CCColors.Red} />
            </View>
            <CCText style={styles.stateCardTitle} textAlign="center">
              Could not load report
            </CCText>
            <CCText style={styles.stateCardBodyError} textAlign="center">
              {typeof apiMsg === 'string'
                ? apiMsg
                : err?.message || 'Request failed. Check login, API URL (localhost:8000), and CORS.'}
            </CCText>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.reportContainer,
          isMobileOrTab ? styles.reportContainerMobile : styles.reportContainerDesktop,
        ]}>
        <OpeningPerformanceReport
          openingScore={frontendView.opening_score}
          openingAccuracy={frontendView.opening_accuracy}
          bestGameScore={frontendView.best_game_score}
          lowestGameScore={frontendView.lowest_game_score}
          reportDate={frontendView.report_date}
          gamesAnalyzed={frontendView.games_analyzed}
          gameScores={frontendView.game_scores}
          gameScoreCards={oirResponse?.data?.game_by_game_performance || []}
          headerRightAction={
            <CCButton
              type="outline"
              disabled={pdfWorking || !oirResponse?.data?.assessment_id}
              onPress={handleDownloadPdf}>
              <CCText>{pdfWorking ? 'Generating PDF…' : 'Download PDF'}</CCText>
            </CCButton>
          }
          selectedOpening={frontendView.selected_opening}
          performanceCardsData={(frontendView.performance_cards || []).map((card: any) => ({
            title: card.title,
            content: card.content,
            tone: card.tone,
          }))}
          reportIntro={frontendView.report_intro}
          scoreBand={frontendView.score_band}
          scoreBandLegend={frontendView.opening_score_band_legend}
          sectionNotes={frontendView.section_notes}
          improvementsData={frontendView.improvements}
          recurringErrorsData={toCountRows(frontendView.recurring_errors)}
          opponentErrorsData={toCountRows(
            frontendView.recurring_opponent_errors || frontendView.opponent_errors,
          )}
          criticalPositionsData={toCountRows(frontendView.critical_positions)}
          modelGamesData={(frontendView.model_games || []).map((g: any) => ({
            title: g.title,
            reason: g.reason,
            model_game_id: g.model_game_id,
            pgn: g.pgn,
            event: g.event,
          }))}
          mostBrokenPrincipleQuote={frontendView.most_broken_principle?.quote}
          principleWiseScores={frontendView.principle_wise_scores}
          colorBreakdown={frontendView.color_breakdown}
          onPracticePrinciples={title => {
            const cannedQuery = oirPrincipleToCannedQuery(title);
            (navigation as any).navigate(
              ScreenName.Puzzles as never,
              {
                oirCannedQuery: cannedQuery,
                cannedQueriesSelected: [cannedQuery],
                playerTypeSelected: 'User',
                cannedQueries: false,
                assessment_id: oirResponse?.data?.assessment_id,
                area: 'opportunity',
              } as never,
            );
          }}
          onSolvePatternDrills={() => {
            (navigation as any).navigate(
              ScreenName.Puzzles as never,
              {
                oirCannedQuery: 'opening_principle',
                cannedQueriesSelected: ['opening_principle'],
                playerTypeSelected: 'User',
                cannedQueries: false,
                assessment_id: oirResponse?.data?.assessment_id,
                area: 'opportunity',
              } as never,
            );
          }}
          onTrainPunishments={() => {
            (navigation as any).navigate(
              ScreenName.Puzzles as never,
              {
                oirCannedQuery: 'opening_principle',
                cannedQueriesSelected: ['opening_principle'],
                playerTypeSelected: 'User',
                cannedQueries: false,
                puzzleTypeSelected: ['Find Opponent Response to my Mistakes'],
                assessment_id: oirResponse?.data?.assessment_id,
                area: 'mistake',
              } as never,
            );
          }}
          onOpenGameReviewFromScore={gameUuid => {
            (navigation as any).navigate(
              ScreenName.ChessAnalysis as never,
              {game_id: gameUuid, viewgame: true} as never,
            );
          }}
          onOpenModelGame={index => {
            const game = frontendView?.model_games?.[index];
            if (!game?.model_game_id) return;
            (navigation as any).navigate(
              ScreenName.ChessAnalysis as never,
              {
                viewgame: true,
                model_game_id: game?.model_game_id,
              } as never,
            );
          }}
          onBackPress={() => {
            navigation.navigate(ScreenName.Dashboard as never)
          }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: CCColors.Primary.Light,
    justifyContent: 'center',
  },
  stateRoot: {
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    backgroundColor: CCColors.Primary.Light,
  },
  stateScroll: {
    flexGrow: 1,
    width: '100%',
    paddingHorizontal: 20,
    paddingVertical: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stateCard: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingVertical: 28,
    paddingHorizontal: 22,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.08)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 4,
  },
  stateIconWrap: {
    alignSelf: 'center',
    marginBottom: 16,
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stateCardTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 12,
    color: CCColors.TextColor.Primary,
    width: '100%',
  },
  stateCardBody: {
    fontSize: 15,
    lineHeight: 24,
    color: CCColors?.TextColor?.Dark ?? 'rgba(77, 63, 55, 0.88)',
    width: '100%',
  },
  stateCardBodyError: {
    fontSize: 15,
    lineHeight: 24,
    color: CCColors.Red,
    width: '100%',
  },
  secondaryButton: {
    marginTop: 24,
    alignSelf: 'stretch',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.15)',
    backgroundColor: 'rgba(250, 246, 235, 0.9)',
  },
  secondaryButtonPressed: {
    opacity: 0.85,
  },
  secondaryButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
  },
  loadingBlock: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  reportContainer: {
    flexGrow: 0,
  },
  reportContainerDesktop: {
    width: '70%',
    maxWidth: 1100,
  },
  reportContainerMobile: {
    width: '100%',
    maxWidth: '100%',
    paddingHorizontal: 8,
  },
  mono: {
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'System',
  },
  loadingLabel: {
    marginTop: 12,
    fontSize: 15,
    color: CCColors.TextColor.Primary,
  },
});

export default OpeningPerformanceReportScreen;